import React from 'react';
import cx from 'classnames';

import {
  Analytics,
  ActionSubjectIdType,
  SourceType,
} from '@trello/atlassian-analytics';

import { BackIcon } from '@trello/nachos/icons/back';
import { Button } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
import { ForwardIcon } from '@trello/nachos/icons/forward';

import { ViewHeaderTestIds } from '@trello/test-ids';

import { forTemplate } from 'app/common/lib/util/i18n';

import {
  GroupByOption,
  NavigationDirection,
  ViewType,
  ZoomLevel,
  BetaPhase,
} from './types';
import { ViewHeaderDropdown } from './ViewHeaderDropdown';
import { ViewsFeedbackHeader } from './ViewsFeedbackHeader';

import styles from './ViewHeader.less';

const format = forTemplate('views');

interface ViewHeaderProps {
  viewName: ViewType;
  dateText: string;
  closeView: () => void;
  onNavigate: (direction: NavigationDirection) => void;
  // if we are "refetching" because a change not detected by our current
  // cache syncing was made.
  isLoading?: boolean;
  // Zoom options
  isZoomLevelEnabled?: boolean;
  currentZoom?: ZoomLevel;
  zoomOptions?: ZoomLevel[];
  changeZoom?: (zoom: ZoomLevel) => void;
  // Group By Options
  isGroupByEnabled?: boolean;
  currentGroupBy?: GroupByOption;
  groupByOptions?: GroupByOption[];
  changeGrouping?: (zoom: GroupByOption) => void;
  hideUnscheduledPopover?: () => void;
  analyticsContainers: {
    board?: { id: string | null };
    organization?: { id: string | null };
    enterprise?: { id: string | null };
  };
  feedbackLink?: string;
  orgId?: string | null;
  betaPhase?: BetaPhase;
}

export const ViewHeader: React.FunctionComponent<ViewHeaderProps> = ({
  viewName,
  dateText,
  closeView,
  onNavigate,
  isLoading = false,
  isZoomLevelEnabled = false,
  currentZoom = null,
  zoomOptions = [],
  changeZoom = () => {},
  isGroupByEnabled = false,
  currentGroupBy = null,
  groupByOptions = [],
  changeGrouping = () => {},
  hideUnscheduledPopover,
  analyticsContainers,
  feedbackLink,
  orgId,
  betaPhase,
}) => {
  let viewScreen: SourceType = 'calendarViewScreen';
  let zoomInlineDialog: SourceType = 'calendarZoomLevelInlineDialog';
  let closeButton: ActionSubjectIdType = 'calendarCloseViewButton';
  let zoomDropdownItem: ActionSubjectIdType = 'calendarZoomLevelDropdownItem';
  let navLeftButton: ActionSubjectIdType = 'calendarScrollLeftButton';
  let navRightButton: ActionSubjectIdType = 'calendarScrollRightButton';
  let navTodayButton: ActionSubjectIdType = 'calendarScrollToTodayButton';
  if (viewName === ViewType.TIMELINE) {
    viewScreen = 'timelineViewScreen';
    closeButton = 'timelineCloseViewButton';
    zoomDropdownItem = 'timelineZoomLevelDropdownItem';
    zoomInlineDialog = 'timelineZoomLevelInlineDialog';
    navLeftButton = 'timelineScrollLeftButton';
    navRightButton = 'timelineScrollRightButton';
    navTodayButton = 'timelineScrollToTodayButton';
  }

  const trackHeaderButtonClick = (buttonName: ActionSubjectIdType) => {
    Analytics.sendClickedButtonEvent({
      buttonName,
      source: viewScreen,
      containers: analyticsContainers,
      attributes: {
        grouping: currentGroupBy,
        currentZoom,
      },
    });
  };

  const handleCloseButtonClick = () => {
    closeView();
    trackHeaderButtonClick(closeButton);
  };

  const renderZoom = () => {
    if (isZoomLevelEnabled && currentZoom) {
      return (
        <>
          <span className={styles.divider}></span>
          <ViewHeaderDropdown
            options={zoomOptions}
            currentOption={currentZoom}
            onChangeOption={changeZoom}
            hideUnscheduledPopover={hideUnscheduledPopover}
            sendTrackingEvent={(zoomLevel: ZoomLevel) => {
              Analytics.sendUIEvent({
                action: 'clicked',
                actionSubject: 'dropdownItem',
                actionSubjectId: zoomDropdownItem,
                source: zoomInlineDialog,
                containers: analyticsContainers,
                attributes: {
                  zoomLevel,
                },
              });
            }}
          />
        </>
      );
    }
  };

  const renderGrouping = () => {
    if (isGroupByEnabled && currentGroupBy) {
      return (
        <ViewHeaderDropdown
          options={groupByOptions}
          currentOption={currentGroupBy}
          onChangeOption={changeGrouping}
          hideUnscheduledPopover={hideUnscheduledPopover}
          sendTrackingEvent={(groupBy: GroupByOption) => {
            Analytics.sendUIEvent({
              action: 'clicked',
              actionSubject: 'dropdownItem',
              actionSubjectId: 'timelineGroupByDropdownItem',
              source: 'timelineGroupByInlineDialog',
              containers: analyticsContainers,
              attributes: {
                groupBy,
              },
            });
          }}
        />
      );
    }
  };

  return (
    <div className={styles.headerContainer}>
      <div className={cx(styles.headerSection, styles.leftSection)}>
        <div className={styles.dateText}>{dateText}</div>
        <div className={styles.navigationButtons}>
          <Button
            onClick={() => {
              onNavigate(NavigationDirection.PREV);
              trackHeaderButtonClick(navLeftButton);
            }}
            className={styles.navigationButton}
            data-test-id={ViewHeaderTestIds.PreviousButton}
            iconBefore={<BackIcon size="small" />}
          />
          <Button
            onClick={() => {
              onNavigate(NavigationDirection.TODAY);
              trackHeaderButtonClick(navTodayButton);
            }}
            className={styles.navigationButton}
            data-test-id={ViewHeaderTestIds.TodayButton}
          >
            {format('today')}
          </Button>
          <Button
            onClick={() => {
              onNavigate(NavigationDirection.NEXT);
              trackHeaderButtonClick(navRightButton);
            }}
            className={styles.navigationButton}
            data-test-id={ViewHeaderTestIds.NextButton}
            iconBefore={<ForwardIcon size="small" />}
          />
          {renderZoom()}
          {renderGrouping()}
        </div>
        {isLoading && <span className={styles.loading}>Loading...</span>}
      </div>
      <div className={cx(styles.headerSection, styles.rightSection)}>
        {feedbackLink && (
          <ViewsFeedbackHeader
            feedbackLink={feedbackLink}
            orgId={orgId}
            betaPhase={betaPhase}
          />
        )}
        <div
          className={styles.closeButton}
          data-test-id={ViewHeaderTestIds.CloseButton}
          onClick={handleCloseButtonClick}
          tabIndex={0}
          role="button"
        >
          <CloseIcon size="large" color="dark" />
        </div>
      </div>
    </div>
  );
};
